import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { labAPI } from '../../utils/api';

const PRIORITY_COLOR = {
    STAT:    { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    Urgent:  { bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
    Routine: { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
};

// Generate CODE128B barcode as SVG inline
function code128Svg(text) {
    const P = [
        '11011001100','11001101100','11001100110','10010011000','10010001100','10001001100',
        '10011001000','10011000100','10001100100','11001001000','11001000100','11000100100',
        '10110011100','10011011100','10011001110','10111001100','10011101100','10011100110',
        '11001110010','11001011100','11001001110','11011100100','11001110100','11101101110',
        '11101001100','11100101100','11100100110','11101100100','11100110100','11100110010',
        '11011011000','11011000110','11000110110','10100011000','10001011000','10001000110',
        '10110001000','10001101000','10001100010','11010001000','11000101000','11000100010',
        '10110111000','10110001110','10001101110','10111011000','10111000110','10001110110',
        '11101110110','11010001110','11000101110','11011101000','11011100010','11011101110',
        '11101011000','11101000110','11100010110','11101101000','11101100010','11100011010',
        '11101111010','11001000010','11110001010','10100110000','10100001100','10010110000',
        '10010000110','10000101100','10000100110','10110010000','10110000100','10011010000',
        '10011000010','10000110100','10000110010','11000010010','11001010000','11110111010',
        '11000010100','10001111010','10100111100','10010111100','10010011110','10111100100',
        '10011110100','10011110010','11110100100','11110010100','11110010010','11011011110',
        '11011110110','11110110110','10101111000','10100011110','10001011110','10111101000',
        '10111100010','11110101000','11110100010','10111011110','10111101110','11101011110',
        '11110101110','11010000100','11010010000','11010011100','1100011101011'
    ];
    const START_B = 104, STOP = 106;
    const vals = text.split('').map(c => c.charCodeAt(0) - 32);
    let check = START_B;
    vals.forEach((v, i) => { check += v * (i + 1); });
    check = check % 103;
    const symbols = [START_B, ...vals, check, STOP];
    const W = 1.2, H = 18;
    let x = 0, rects = '';
    symbols.forEach(sym => {
        const pat = P[sym];
        if (!pat) return;
        let cx = x;
        for (let i = 0; i < pat.length; i++) {
            if (pat[i] === '1') rects += `<rect x="${cx.toFixed(2)}" y="0" width="${W}" height="${H}"/>`;
            cx += W;
        }
        x += pat.length * W;
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${x.toFixed(2)}" height="${H}" viewBox="0 0 ${x.toFixed(2)} ${H}" style="display:block;width:100%;height:18px;"><g fill="black">${rects}</g></svg>`;
}

function printBarcodes(order) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });

    const labelsHTML = order.items.map(item => `
        <div class="label">
            <div class="hospital">HIMS</div>
            <div class="patient">${order.full_name}</div>
            <div class="meta">MRN: ${order.mrn} &nbsp;|&nbsp; ${order.age_years}y ${order.gender}</div>
            <div class="divider"></div>
            <div class="test-name">${item.test_name}</div>
            <div class="meta">${item.test_code} | ${item.specimen_type}</div>
            <div class="divider"></div>
            <svg class="barcode" id="bc-${item.id}"></svg>
            <div class="sample-id">${item.sample_id}</div>
            <div class="meta">${order.order_number} | ${dateStr} ${timeStr}</div>
        </div>
    `).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <script src="http://localhost:3000/jsbarcode.min.js"><\/script>
    <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        html, body { margin:0; padding:0; width:50mm; background:#fff; font-family:'Courier New',monospace; }
        .label { width:50mm; height:23mm; border:1px solid #000; padding:2px 3px; display:flex; flex-direction:column; justify-content:center; overflow:hidden; break-after:page; page-break-after:always; }
        .label:last-child { break-after:avoid; page-break-after:avoid; }
        .hospital { font-size:6px; font-weight:900; text-align:center; }
        .patient { font-size:8px; font-weight:900; text-align:center; }
        .test-name { font-size:7px; font-weight:900; text-align:center; word-break:break-word; }
        .meta { font-size:6px; font-weight:700; text-align:center; color:#333; }
        .sample-id { font-size:6px; font-weight:900; text-align:center; }
        .divider { border-top:1px dashed #000; margin:1px 0; }
        .barcode { display:block; margin:0 auto; max-width:100%; }
        @media print { @page { size:50mm 25mm; margin:0; } html,body { margin:0; padding:0; } .label { page-break-after:always; } .label:last-child { page-break-after:avoid; } }
    </style>
    </head><body>
    ${labelsHTML}
    <script>
        window.onload = function() {
            ${order.items.map(item => `
                try { var sid='${item.sample_id}'; var parts=sid.split('-'); var short=parts.slice(-2).join('-'); JsBarcode('#bc-${item.id}', short, { format:'CODE128', width:1.5, height:20, displayValue:true, fontSize:7, margin:6, textMargin:1 }); } catch(e) {}
            `).join('')}
            setTimeout(function() { window.print(); }, 600);
        };
    <\/script>
    </body></html>`;

    const win = window.open('', '_blank', 'width=220,height=120');
    if (!win) { alert('Popup blocked — allow popups for this site'); return; }
    win.document.write(html);
    win.document.close();
}

const REJECT_REASONS = ['Hemolyzed', 'Insufficient Quantity', 'Wrong Tube', 'Clotted', 'Leaked', 'Mislabeled', 'Other'];

export default function SampleCollection() {
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collecting, setCollecting] = useState(null);
    const [scanMode, setScanMode] = useState(false);
    const [scanInput, setScanInput] = useState('');
    const [scanResult, setScanResult] = useState(null);
    const [scanLog, setScanLog] = useState([]);
    const scanRef = useRef(null);
    const [rejectModal, setRejectModal] = useState(null);
    const [rejectReason, setRejectReason] = useState('Hemolyzed');
    const [rejecting, setRejecting] = useState(false);
    const [search, setSearch] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await labAPI.getSampleQueue();
            setQueue(res.data);
        } catch { toast.error('Failed to load queue'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { if (scanMode && scanRef.current) scanRef.current.focus(); }, [scanMode]);

    const collect = async (order) => {
        setCollecting(order.id);
        try {
            await labAPI.collectSample(order.id);
            toast.success(`Sample collected for ${order.full_name}`);
            printBarcodes(order);
            load();
        } catch { toast.error('Collection failed'); }
        finally { setCollecting(null); }
    };

    const handleScan = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!scanInput.trim()) return;
        const inputVal = scanInput.trim();
        setScanInput('');
        try {
            const res = await labAPI.scanSample(inputVal);
            const s = res.data.sample;
            const logEntry = {
                id: Date.now(),
                success: true,
                scanned_id: inputVal,
                sample_id: s?.sample_id || inputVal,
                test_name: s?.test_name || '—',
                order_number: s?.order_number || '—',
                time: new Date(),
            };
            setScanLog(prev => [logEntry, ...prev]);
            setScanResult({ success: true, msg: `✅ Confirmed` });
            toast.success('Sample scan recorded');
            if (scanRef.current) scanRef.current.focus();
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Scan failed';
            const logEntry = { id: Date.now(), success: false, scanned_id: inputVal, sample_id: inputVal, test_name: '—', order_number: '—', time: new Date() };
            setScanLog(prev => [logEntry, ...prev]);
            setScanResult({ success: false, msg: `❌ ${errMsg}` });
            toast.error(errMsg);
            if (scanRef.current) scanRef.current.focus();
        }
    };

    const fmt = (dt) => dt ? new Date(dt).toLocaleString('en-PK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

    const rejectSample = async () => {
        if (!rejectModal) return;
        setRejecting(true);
        try {
            await labAPI.rejectSample({ sample_id: rejectModal.sample_id, rejection_reason: rejectReason });
            toast.success(`Sample rejected: ${rejectModal.test_name}`);
            setRejectModal(null);
            setRejectReason('Hemolyzed');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Rejection failed'); }
        finally { setRejecting(false); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🧪 Sample Collection</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Collect samples & print tube barcodes</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input
                        placeholder="🔍 Search by Name, MRN or Order No"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, width: 260 }}
                    />
                    <button onClick={() => { setScanMode(!scanMode); setScanResult(null); }}
                        style={{ padding: '8px 20px', background: scanMode ? '#7c3aed' : 'white', color: scanMode ? 'white' : '#7c3aed', border: '2px solid #7c3aed', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                        {scanMode ? '📋 Queue View' : '📷 Scan Mode'}
                    </button>
                    <button onClick={load} style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
                </div>
            </div>

            {/* Scan Mode */}
            {scanMode && (
                <div style={{ background: 'white', borderRadius: 12, padding: 24, marginBottom: 20, border: '2px solid #7c3aed' }}>
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <div style={{ fontSize: 32, marginBottom: 4 }}>📷</div>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', margin: 0 }}>Scan Sample Barcode</h3>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>Scan barcode or manually enter lab number → Press Confirm</p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, maxWidth: 560, margin: '0 auto 12px' }}>
                        <input ref={scanRef} value={scanInput} onChange={e => setScanInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleScan(e); }}
                            placeholder="Scan barcode or type Sample ID..."
                            style={{ flex: 1, padding: '11px 14px', border: '2px solid #7c3aed', borderRadius: 8, fontSize: 14, outline: 'none' }} />
                        <button type="button" onClick={handleScan}
                            style={{ padding: '11px 22px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                            ✓ Confirm
                        </button>
                        {scanLog.length > 0 && (
                            <button type="button" onClick={() => setScanLog([])}
                                style={{ padding: '11px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                                Clear List
                            </button>
                        )}
                    </div>

                    {/* Scanned Log Table */}
                    {scanLog.length > 0 && (
                        <div style={{ maxWidth: 800, margin: '0 auto', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                            <div style={{ background: '#f8fafc', padding: '8px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>📋 Scanned Samples — {scanLog.length} total</span>
                                <span style={{ fontSize: 11, color: '#64748b' }}>{scanLog.filter(l => l.success).length} collected, {scanLog.filter(l => !l.success).length} failed</span>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ background: '#f1f5f9' }}>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>#</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Sample ID</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Order No.</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Test</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Time</th>
                                        <th style={{ padding: '7px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scanLog.map((log, idx) => (
                                        <tr key={log.id} style={{ borderTop: '1px solid #f1f5f9', background: log.success ? 'white' : '#fef2f2' }}>
                                            <td style={{ padding: '7px 12px', color: '#94a3b8', fontWeight: 700 }}>{scanLog.length - idx}</td>
                                            <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>{log.sample_id}</td>
                                            <td style={{ padding: '7px 12px', color: '#374151' }}>{log.order_number}</td>
                                            <td style={{ padding: '7px 12px', color: '#374151' }}>{log.test_name}</td>
                                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{log.time.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                                            <td style={{ padding: '7px 12px' }}>
                                                {log.success
                                                    ? <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: 11 }}>✅ Collected</span>
                                                    : <span style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: 11 }}>❌ Not Found</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {scanLog.length === 0 && (
                        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 8 }}>No samples scanned yet — scan a barcode or enter Sample ID</p>
                    )}
                </div>
            )}

            {/* Queue */}
            {!scanMode && (
                loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
                : queue.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                        <p style={{ color: '#64748b' }}>No pending sample collections</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {queue.filter(order => {
                            if (!search.trim()) return true;
                            const q = search.trim().toLowerCase();
                            return order.full_name?.toLowerCase().includes(q) ||
                                order.mrn?.toLowerCase().includes(q) ||
                                order.order_number?.toLowerCase().includes(q);
                        }).map(order => {
                            const pc = PRIORITY_COLOR[order.priority] || PRIORITY_COLOR.Routine;
                            const collectedCount = parseInt(order.collected_count) || 0;
                            const totalCount = parseInt(order.test_count) || 0;
                            const allCollected = collectedCount === totalCount;
                            return (
                                <div key={order.id} style={{ background: 'white', borderRadius: 10, border: `1px solid ${order.priority === 'STAT' ? '#fca5a5' : '#e2e8f0'}`, padding: 16 }}>
                                    {/* Order Header */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                            <span style={{ background: pc.bg, color: pc.color, border: `1px solid ${pc.border}`, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                                                {order.priority === 'STAT' ? '🚨 ' : ''}{order.priority}
                                            </span>
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#7c3aed', fontSize: 13 }}>{order.order_number}</div>
                                                <div style={{ fontSize: 12, color: '#64748b' }}>{fmt(order.order_time)}</div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 15, fontWeight: 700 }}>{order.full_name}</div>
                                            <div style={{ fontSize: 12, color: '#64748b' }}>{order.mrn} | {order.age_years}y {order.gender} | {order.phone}</div>
                                        </div>
                                    </div>

                                    {/* Tests list */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginBottom: 14 }}>
                                        {order.items.map(item => (
                                            <div key={item.id} style={{ background: item.status === 'Rejected' ? '#fef2f2' : item.collected_at ? '#f0fdf4' : '#f8fafc', border: `1px solid ${item.status === 'Rejected' ? '#fca5a5' : item.collected_at ? '#86efac' : '#e2e8f0'}`, borderRadius: 7, padding: '8px 12px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{ fontSize: 12, fontWeight: 700 }}>{item.test_name}</div>
                                                    {item.status !== 'Rejected' && item.sample_id && (
                                                        <button onClick={() => { setRejectModal(item); setRejectReason('Hemolyzed'); }}
                                                            style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 7px', cursor: 'pointer' }}>
                                                            ✕ Reject
                                                        </button>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 10, color: '#64748b' }}>{item.test_code} | {item.specimen_type}</div>
                                                <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 2 }}>{item.sample_id}</div>
                                                {item.status === 'Rejected' && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2, fontWeight: 700 }}>✕ REJECTED</div>}
                                                {item.collected_at && item.status !== 'Rejected' && <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2 }}>✅ Collected: {fmt(item.collected_at)}</div>}
                                                {item.processing_started_at && <div style={{ fontSize: 10, color: '#1d4ed8', marginTop: 1 }}>⚙️ Processing: {fmt(item.processing_started_at)}</div>}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                        <span style={{ fontSize: 12, color: '#64748b' }}>
                                            {collectedCount}/{totalCount} collected
                                        </span>
                                        {!allCollected && (
                                            <button onClick={() => collect(order)} disabled={collecting === order.id}
                                                style={{ padding: '8px 20px', background: order.priority === 'STAT' ? '#dc2626' : '#7c3aed', color: 'white', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                                                {collecting === order.id ? 'Collecting...' : '🧪 Collect & Print Barcodes'}
                                            </button>
                                        )}
                                        {allCollected && (
                                            <button onClick={() => printBarcodes(order)}
                                                style={{ padding: '8px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                                                🖨️ Reprint Barcodes
                                            </button>
                                        )}
                                        {allCollected && (
                                            <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                                                ✅ All Collected
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {/* Reject Modal */}
            {rejectModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: 'white', borderRadius: 14, padding: 28, maxWidth: 420, width: '100%' }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>✕ Reject Sample</h3>
                        <p style={{ fontSize: 13, color: '#374151', marginBottom: 16 }}>
                            Test: <strong>{rejectModal.test_name}</strong><br />
                            Sample ID: <span style={{ color: '#7c3aed', fontWeight: 700 }}>{rejectModal.sample_id}</span>
                        </p>
                        <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Rejection Reason</label>
                        <select value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, marginBottom: 20, background: 'white' }}>
                            {REJECT_REASONS.map(r => <option key={r}>{r}</option>)}
                        </select>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setRejectModal(null)}
                                style={{ padding: '8px 18px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                            <button onClick={rejectSample} disabled={rejecting}
                                style={{ padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                                {rejecting ? 'Rejecting...' : 'Confirm Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
